import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import LinearGradient from 'react-native-linear-gradient';
import JobService from '../services/jobService';
import { Job } from '../types/Job';
import { useFirestoreSubscription } from '../hooks/useFirestoreSubscription';
import { formatAmount as fmt } from '../utils/formatAmount';

interface Stats {
  totalRevenue: number;
  totalJobs: number;
  cashJobs: number;
  cashAmount: number;
  debtJobs: number;
  debtAmount: number;
}

function computeStats(jobs: Job[]): Stats {
  const cash = jobs.filter(j => j.paymentType === 'cash');
  const debt = jobs.filter(j => j.paymentType === 'debt');
  return {
    totalRevenue: jobs.reduce((sum, j) => sum + j.amount, 0),
    totalJobs: jobs.length,
    cashJobs: cash.length,
    cashAmount: cash.reduce((sum, j) => sum + j.amount, 0),
    debtJobs: debt.length,
    debtAmount: debt.reduce((sum, j) => sum + j.amount, 0),
  };
}


const JobSummaryCard: React.FC = () => {
  const { data: jobs, loading } = useFirestoreSubscription(
    (onUpdate, onError) => JobService.subscribeToAllJobs(onUpdate, onError),
    [] as Job[],
  );
  const completedJobs = useMemo(() => jobs.filter(j => j.status === 'completed'), [jobs]);
  const stats = useMemo(() => computeStats(completedJobs), [completedJobs]);
  const s = loading ? null : stats;

  return (
    <View style={styles.wrapper}>
    <LinearGradient
      colors={['#063669', '#274e82']}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.card}
    >
      {s === null ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator
            testID="activity-indicator"
            size="small"
            color="white"
          />
        </View>
      ) : (
        <>
          <View style={styles.heroRow}>
            <View style={styles.heroLeft}>
              <Text style={styles.heroLabel}>All-Time Revenue</Text>
              <Text style={styles.heroAmount}>{fmt(s.totalRevenue)}</Text>
            </View>
            <View style={styles.badge}>
              <Text style={styles.badgeCount}>{s.totalJobs}</Text>
              <Text style={styles.badgeLabel}>jobs</Text>
            </View>
          </View>

          <View style={styles.tilesRow}>
            <View style={styles.tile}>
              <Text style={styles.tileLabel}>Cash</Text>
              <Text style={styles.tileJobCount}>{s.cashJobs} jobs</Text>
              <Text style={styles.tileAmount}>{fmt(s.cashAmount)}</Text>
            </View>

            <View style={[styles.tile, styles.debtTile]}>
              <Text style={styles.tileLabel}>Debt</Text>
              <Text style={[styles.tileJobCount, styles.debtText]}>
                {s.debtJobs} jobs
              </Text>
              <Text style={[styles.tileAmount, styles.debtText]}>
                {fmt(s.debtAmount)}
              </Text>
            </View>
          </View>
        </>
      )}
    </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    paddingHorizontal: 20,
  },
  card: {
    borderRadius: 6,
    marginBottom: 12,
    padding: 20,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.06,
    shadowRadius: 40,
    elevation: 4,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  heroRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  heroLeft: {
    flex: 1,
  },
  heroLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: 'rgba(255,255,255,0.7)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  heroAmount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 34,
    color: 'white',
  },
  badge: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignItems: 'center',
  },
  badgeCount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 18,
    color: 'white',
    lineHeight: 22,
  },
  badgeLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: 'rgba(255,255,255,0.8)',
  },
  tilesRow: {
    flexDirection: 'row',
    gap: 10,
  },
  tile: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 6,
    padding: 14,
  },
  debtTile: {
    borderWidth: 1,
    borderColor: 'rgba(255,180,100,0.4)',
  },
  tileLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: 'rgba(255,255,255,0.75)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  tileJobCount: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 16,
    color: 'white',
    lineHeight: 20,
  },
  tileAmount: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    marginTop: 2,
  },
  debtText: {
    color: '#ffb59a',
  },
});

export default JobSummaryCard;
